import React, { useState, useEffect, useCallback } from 'react';
import { Plus, FileText, CheckCircle, XCircle, AlertCircle, Calendar, DollarSign, User, Home, CreditCard, Building2, MapPin, ChevronDown, ChevronUp, RefreshCw, ArrowRightLeft, Search } from 'lucide-react';
import { useForm } from 'react-hook-form';
import api from '../services/api';
import Modal from '../components/Common/Modal';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import PaymentModal from '../components/PaymentModal';
import LeaseDetailsModal from '../components/LeaseDetailsModal';
import TransferModal from '../components/TransferModal';

const PAGE_SIZE = 20;

const Leases = () => {
    const [leases, setLeases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showRenewModal, setShowRenewModal] = useState(false);
    const [selectedLeaseForRenew, setSelectedLeaseForRenew] = useState(null);
    const [assets, setAssets] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [expandedAsset, setExpandedAsset] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedLeaseForPayment, setSelectedLeaseForPayment] = useState(null);
    const [showLeaseDetailsModal, setShowLeaseDetailsModal] = useState(false);
    const [selectedLeaseForDetails, setSelectedLeaseForDetails] = useState(null);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [selectedLeaseForTransfer, setSelectedLeaseForTransfer] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [renewFormData, setRenewFormData] = useState({
        new_end_date: '',
        rent_adjustment: 0,
        reason: ''
    });
    const [statusFilter, setStatusFilter] = useState('all');
    const [totalCount, setTotalCount] = useState(0);
    const { register, handleSubmit, reset, watch, setValue } = useForm();

    
    const startDate = watch('start_date');
    const durationMonths = watch('duration_months');
    
    // Calculate end date based on duration
    const calculateEndDate = (startDate, durationMonths) => {
        if (!startDate || !durationMonths) return '';
        const date = new Date(startDate);
        date.setMonth(date.getMonth() + parseInt(durationMonths));
        return date.toISOString().split('T')[0];
    };
    
    useEffect(() => {
        if (startDate && durationMonths) {
            const endDate = calculateEndDate(startDate, durationMonths);
            setValue('end_date', endDate);
        }
    }, [startDate, durationMonths, setValue]);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchLeases();
        fetchAssetsWithUnits();
        fetchTenants();
    }, []);

    // Re-fetch when page, status filter, or debounced search changes
    useEffect(() => {
        fetchLeases();
    }, [page, statusFilter, debouncedSearch]);

    const fetchLeases = useCallback(async () => {
    try {
        setLoading(true);
        console.log('Fetching leases page', page, 'status:', statusFilter);
        
        let url = `/leases?page=${page}&limit=${PAGE_SIZE}`;
        if (statusFilter !== 'all') url += `&status=${statusFilter}`;
        if (debouncedSearch) url += `&search=${encodeURIComponent(debouncedSearch)}`;
        
        const response = await api.get(url);
        console.log('Raw leases response:', response.data);
        
        if (response.data.success && response.data.data) {
            const leasesData = response.data.data.data || response.data.data || [];
            const pagination = response.data.data.pagination;
            
            setLeases(Array.isArray(leasesData) ? leasesData : []);
            
            if (pagination) {
                setTotalPages(pagination.pages || 1);
                setTotalItems(pagination.total || 0);
            } else {
                // Fallback if no pagination
                setTotalPages(1);
                setTotalItems(Array.isArray(leasesData) ? leasesData.length : 0);
            }
        } else {
            console.warn('Unexpected response structure:', response.data);
            setLeases([]);
            setTotalPages(1);
            setTotalItems(0);
        }
    } catch (error) {
        console.error('Error fetching leases:', error);
        setLeases([]);
    } finally {
        setLoading(false);
    }
}, [page, statusFilter, debouncedSearch]);

    const fetchAssetsWithUnits = async () => {
    try {
        const assetsResponse = await api.get('/assets');
        const allAssets = assetsResponse.data.data.data || [];
        
        // Fetch active leases once (reused for all assets)
        let activeLeases = [];
        try {
            const leasesResponse = await api.get('/leases?status=active&limit=100');
            activeLeases = leasesResponse.data.data.data || [];
        } catch (e) {
            console.warn('Could not fetch active leases:', e);
        }
        const rentedUnitIds = new Set(activeLeases.map(l => l.asset_unit_id));
        
        // Fetch all units in parallel
        const unitResponses = await Promise.all(
            allAssets.map(asset =>
                api.get(`/assets/${asset.id}/units`).catch(() => ({ data: { data: [] } }))
            )
        );
        
        const assetsWithUnits = allAssets.map((asset, idx) => {
            const units = unitResponses[idx]?.data?.data || [];
            const availableUnits = units.filter(u => u.status === 'available' && !rentedUnitIds.has(u.id));
            const rentedUnits = units.filter(u => u.status === 'rented' || rentedUnitIds.has(u.id));
            const maintenanceUnits = units.filter(u => u.status === 'maintenance');
            
            return {
                ...asset,
                units,
                availableUnits: availableUnits.length,
                rentedUnits: rentedUnits.length,
                maintenanceUnits: maintenanceUnits.length,
                totalUnits: units.length
            };
        });
        
        setAssets(assetsWithUnits);
    } catch (error) {
        console.error('Error fetching assets:', error);
        setAssets([]);
    }
};


    const fetchTenants = async () => {
        try {
            const response = await api.get('/tenants/users?role=tenant');
            setTenants(response.data.data.data || []);
        } catch (error) {
            console.error('Error fetching tenants:', error);
        }
    };

    const onPageChange = (newPage) => {
        setPage(newPage);
    };

    const onCreateLease = async (data) => {
    try {
        setSubmitting(true);
        
        if (!data.user_id) {
            alert('Please select a tenant');
            setSubmitting(false);
            return;
        }
        
        if (!data.asset_unit_id) {
            alert('No unit selected');
            setSubmitting(false);
            return;
        }
        
        if (!data.start_date) {
            alert('Please select a start date');
            setSubmitting(false);
            return;
        }
        
        if (!data.rent_amount || data.rent_amount <= 0) {
            alert('Please enter a valid rent amount');
            setSubmitting(false);
            return;
        }
        
        let endDate = data.end_date;
        if (!endDate && data.start_date && data.duration_months) {
            const start = new Date(data.start_date);
            endDate = new Date(start);
            endDate.setMonth(start.getMonth() + parseInt(data.duration_months));
            endDate = endDate.toISOString().split('T')[0];
        }
        
        const leaseData = {
            user_id: parseInt(data.user_id),
            asset_unit_id: parseInt(data.asset_unit_id),
            start_date: data.start_date,
            end_date: endDate,
            billing_cycle: data.billing_cycle || 'monthly',
            rent_amount: parseFloat(data.rent_amount),
            deposit_amount: parseFloat(data.deposit_amount) || 0,
            payment_due_day: parseInt(data.payment_due_day) || 1,
            late_fee_percentage: parseFloat(data.late_fee_percentage) || 5,
            terms_and_conditions: data.terms || ''
        };
        
        const response = await api.post('/leases', leaseData);
        
        alert('Lease created successfully!');
        
        reset();
        setShowModal(false);
        setSelectedAsset(null);
        fetchLeases();
        fetchAssetsWithUnits();
        
    } catch (error) {
        console.error('Error creating lease:', error);
        alert(error.response?.data?.error || error.response?.data?.message || 'Failed to create lease');
    } finally {
        setSubmitting(false);
    }
};

    const openLeaseModal = (asset, unit = null) => {
        setSelectedAsset({ ...asset, selectedUnit: unit });
        setShowModal(true);
        if (unit && unit.rate_override) {
            setValue('rent_amount', unit.rate_override);
        } else if (asset.base_rate) {
            setValue('rent_amount', asset.base_rate);
        }
    };
    
    const viewPayments = (lease) => {
        setSelectedLeaseForPayment(lease);
        setShowPaymentModal(true);
    };
    
    const viewLeaseDetails = (lease) => {
        if (!lease || !lease.id) {
            console.error('Invalid lease data:', lease);
            return;
        }
        console.log('Opening details for lease:', lease);
        setSelectedLeaseForDetails(lease);
        setShowLeaseDetailsModal(true);
    };

    const openTransferModal = (lease) => {
        setSelectedLeaseForTransfer(lease);
        setShowTransferModal(true);
    };
    
    // Lease Extension/Renewal Function
    const openRenewModal = (lease) => {
        setSelectedLeaseForRenew(lease);
        const currentEndDate = new Date(lease.end_date);
        // Default to adding 12 months
        const newEndDate = new Date(currentEndDate);
        newEndDate.setMonth(currentEndDate.getMonth() + 12);
        setRenewFormData({
            new_end_date: newEndDate.toISOString().split('T')[0],
            rent_adjustment: 0,
            reason: ''
        });
        setShowRenewModal(true);
    };

    const handleRenewLease = async () => {
        if (!renewFormData.new_end_date) {
            alert('Please select a new end date');
            return;
        }
        
        setSubmitting(true);
        try {
            const response = await api.post(`/leases/${selectedLeaseForRenew.id}/renew`, {
                new_end_date: renewFormData.new_end_date,
                rent_adjustment: parseFloat(renewFormData.rent_adjustment) || 0,
                reason: renewFormData.reason
            });
            
            console.log('Lease renewed:', response.data);
            alert('Lease renewed successfully!');
            setShowRenewModal(false);
            setSelectedLeaseForRenew(null);
            fetchLeases();
            fetchAssetsWithUnits();
        } catch (error) {
            console.error('Error renewing lease:', error);
            alert(error.response?.data?.error || 'Failed to renew lease');
        } finally {
            setSubmitting(false);
        }
    };
    
    // Activate a draft lease
    const activateLease = async (leaseId) => {
        if (window.confirm('Are you sure you want to activate this lease? This will mark the unit as rented.')) {
            try {
                const leaseResponse = await api.get(`/leases/${leaseId}`);
                const lease = leaseResponse.data.data;
                
                const response = await api.put(`/leases/${leaseId}`, { status: 'active' });
                console.log('Lease activated:', response.data);
                
                if (lease.asset_unit_id) {
                    await api.put(`/assets/units/${lease.asset_unit_id}`, { status: 'rented' });
                    console.log('Unit status updated to rented');
                }
                
                alert('Lease activated successfully!');
                fetchLeases();
                fetchAssetsWithUnits();
            } catch (error) {
                console.error('Error activating lease:', error);
                alert('Failed to activate lease: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    // Sign a lease
    const signLease = async (leaseId) => {
        const signature = prompt('Enter your signature or click OK to use digital signature:');
        if (signature) {
            try {
                const response = await api.post(`/leases/${leaseId}/sign`, { signature });
                console.log('Lease signed:', response.data);
                alert('Lease signed successfully!');
                fetchLeases();
            } catch (error) {
                console.error('Error signing lease:', error);
                alert('Failed to sign lease: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    // Terminate an active lease
    const terminateLease = async (leaseId) => {
        const reason = prompt('Please provide a reason for termination:');
        if (reason) {
            if (window.confirm('Are you sure you want to terminate this lease? This action cannot be undone.')) {
                try {
                    const response = await api.post(`/leases/${leaseId}/terminate`, { reason });
                    console.log('Lease terminated:', response.data);
                    alert('Lease terminated successfully!');
                    fetchLeases();
                    fetchAssetsWithUnits();
                } catch (error) {
                    console.error('Error terminating lease:', error);
                    alert('Failed to terminate lease: ' + (error.response?.data?.error || error.message));
                }
            }
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' },
            draft: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Draft' },
            signed: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle, label: 'Signed' },
            terminated: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Terminated' },
            expired: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Expired' }
        };
        const badge = badges[status] || badges.draft;
        const Icon = badge.icon;
        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                <Icon className="h-3 w-3 mr-1" />
                {badge.label}
            </span>
        );
    };

    // Check if lease is near expiration (within 30 days)
    const isLeaseExpiring = (endDate) => {
        const today = new Date();
        const end = new Date(endDate);
        const daysDiff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
        return daysDiff <= 30 && daysDiff > 0;
    };

    if (loading) return <LoadingSpinner size="lg" />;

    return (
        <div className="space-y-6">
            {/* Header with search */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Leases</h1>
                    <p className="text-gray-600">Manage lease agreements and contracts</p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Search bar */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search leases..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 w-48 sm:w-56"
                        />
                    </div>
                    <button
                        onClick={fetchLeases}
                        className="flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                    </button>
                </div>
            </div>
            
            {/* Assets with Units Section */}
            <div className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">Available Properties & Units</h2>

                {assets.map((asset) => (
                    <div key={asset.id} className="bg-white rounded-lg shadow overflow-hidden">
                        <div 
                            className="p-6 border-b cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => setExpandedAsset(expandedAsset === asset.id ? null : asset.id)}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <div className="text-gray-500">
                                        {expandedAsset === asset.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                                    </div>
                                    <div>
                                        <div className="flex items-center space-x-2">
                                            <Building2 className="h-5 w-5 text-indigo-600" />
                                            <h3 className="text-lg font-semibold text-gray-900">{asset.name}</h3>
                                            <span className="text-sm text-gray-500 capitalize">({asset.category})</span>
                                        </div>
                                        {asset.description && (
                                            <p className="text-sm text-gray-600 mt-1">{asset.description}</p>
                                        )}
                                        <div className="flex items-center space-x-4 mt-2">
                                            <div className="flex items-center space-x-1 text-sm text-gray-500">
                                                <MapPin className="h-4 w-4" />
                                                <span>Location: {asset.location_lat && asset.location_lng ? `${asset.location_lat}, ${asset.location_lng}` : 'Not specified'}</span>
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                Base Rate: <span className="font-semibold text-indigo-600">KES {asset.base_rate?.toLocaleString()}</span>/month
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-6">
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-gray-900">{asset.totalUnits || 0}</p>
                                        <p className="text-xs text-gray-500">Total Units</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-green-600">{asset.availableUnits || 0}</p>
                                        <p className="text-xs text-gray-500">Available</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-blue-600">{asset.rentedUnits || 0}</p>
                                        <p className="text-xs text-gray-500">Rented</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-orange-600">{asset.maintenanceUnits || 0}</p>
                                        <p className="text-xs text-gray-500">Maintenance</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {expandedAsset === asset.id && (
                            <div className="p-6 bg-gray-50">
                                <h4 className="font-semibold text-gray-900 mb-4">Units</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {asset.units && asset.units.map((unit) => (
                                        <div key={unit.id} className="bg-white rounded-lg p-4 shadow-sm border">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h5 className="font-medium text-gray-900">Unit {unit.unit_number}</h5>
                                                    {unit.name && <p className="text-sm text-gray-500">{unit.name}</p>}
                                                </div>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                    unit.status === 'available' ? 'bg-green-100 text-green-800' :
                                                    unit.status === 'rented' ? 'bg-blue-100 text-blue-800' :
                                                    'bg-orange-100 text-orange-800'
                                                }`}>
                                                    {unit.status}
                                                </span>
                                            </div>
                                            <div className="mt-2 pt-2 border-t">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="text-sm text-gray-600">Monthly Rate:</span>
                                                    <span className="font-semibold">
                                                        {unit.rate_override ? (
                                                            <span>
                                                                KES {unit.rate_override.toLocaleString()}
                                                                <span className="text-xs text-gray-500 ml-1">(custom)</span>
                                                            </span>
                                                        ) : (
                                                            `KES ${asset.base_rate?.toLocaleString()}`
                                                        )}
                                                    </span>
                                                </div>
                                                {unit.status === 'available' && (
                                                    <button
                                                        onClick={() => openLeaseModal(asset, unit)}
                                                        className="w-full mt-2 px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
                                                    >
                                                        Create Lease for Unit {unit.unit_number}
                                                    </button>
                                                )}
                                                {unit.status === 'rented' && unit.current_lease && (
                                                    <div className="mt-2 text-sm text-gray-500">
                                                        <p>Tenant: {unit.current_lease.first_name} {unit.current_lease.last_name}</p>
                                                        <p>Lease until: {new Date(unit.current_lease.end_date).toLocaleDateString()}</p>
                                                        {isLeaseExpiring(unit.current_lease.end_date) && (
                                                            <p className="text-xs text-orange-600 mt-1">⚠️ Expiring soon</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {(!asset.units || asset.units.length === 0) && (
                                    <div className="text-center py-8 text-gray-500">
                                        No units added for this property yet.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Status Filter Tabs */}
            <div className="mt-8 flex flex-wrap gap-2">
                {['all', 'active', 'draft', 'terminated', 'expired'].map(status => (
                    <button
                        key={status}
                        onClick={() => { setStatusFilter(status); setPage(1); }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            statusFilter === status
                                ? 'bg-indigo-600 text-white shadow'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                        <span className="ml-1 text-xs opacity-75">({/* total count used from pagination */})</span>
                    </button>
                ))}
            </div>

            {/* Leases Table */}
            <div className="mt-4">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    {statusFilter === 'all' ? 'All Leases' : `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Leases`} ({totalItems})
                </h2>
                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '800px' }}>
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[100px]">Lease #</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[140px]">Tenant</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[130px]">Property & Unit</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[140px]">Period</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[90px]">Rent</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[70px]">Status</th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {leases.map((lease) => (
                                <tr key={lease.id} className="hover:bg-gray-50">
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <FileText className="h-4 w-4 text-gray-400 mr-1 flex-shrink-0" />
                                            <span className="text-xs font-medium text-gray-900 truncate max-w-[80px]" title={lease.lease_number}>{lease.lease_number}</span>
                                        </div>
                                    </td>
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <p className="text-xs font-medium text-gray-900 truncate max-w-[130px]" title={`${lease.first_name} ${lease.last_name}`}>{lease.first_name} {lease.last_name}</p>
                                        <p className="text-xs text-gray-500 truncate max-w-[130px]" title={lease.email}>{lease.email}</p>
                                    </td>
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <p className="text-xs font-medium text-gray-900 truncate max-w-[120px]" title={lease.asset_name}>{lease.asset_name}</p>
                                        <p className="text-xs text-gray-500">Unit: {lease.unit_number || 'Main'}</p>
                                    </td>
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <p className="text-xs text-gray-900">
                                            {new Date(lease.start_date).toLocaleDateString()} - {new Date(lease.end_date).toLocaleDateString()}
                                        </p>
                                        {isLeaseExpiring(lease.end_date) && (
                                            <p className="text-xs text-orange-600">⚠️ Expires soon</p>
                                        )}
                                    </td>
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <p className="text-xs font-medium text-gray-900">KES {parseFloat(lease.rent_amount).toLocaleString()}</p>
                                    </td>
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        {getStatusBadge(lease.status)}
                                    </td>
                                     <td className="px-2 py-3 whitespace-nowrap">
                                        <div className="flex gap-1">
                                            {lease.status === 'draft' && (
                                                <>
                                                    <button
                                                        onClick={() => activateLease(lease.id)}
                                                        className="px-1.5 py-0.5 bg-green-600 text-white text-[10px] rounded hover:bg-green-700"
                                                        title="Activate Lease"
                                                    >
                                                        Activate
                                                    </button>
                                                    <button
                                                        onClick={() => signLease(lease.id)}
                                                        className="px-1.5 py-0.5 bg-blue-600 text-white text-[10px] rounded hover:bg-blue-700"
                                                        title="Sign Lease"
                                                    >
                                                        Sign
                                                    </button>
                                                </>
                                            )}
                                            {lease.status === 'active' && (
                                                <>
                                                    <button
                                                        onClick={() => viewLeaseDetails(lease)}
                                                        className="px-1.5 py-0.5 bg-indigo-600 text-white text-[10px] rounded hover:bg-indigo-700"
                                                        title="View Details"
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        onClick={() => openTransferModal(lease)}
                                                        className="px-1.5 py-0.5 bg-indigo-600 text-white text-[10px] rounded hover:bg-indigo-700"
                                                        title="Transfer Tenant to Another Unit"
                                                    >
                                                        Transfer
                                                    </button>
                                                    <button
                                                        onClick={() => openRenewModal(lease)}
                                                        className="px-1.5 py-0.5 bg-purple-600 text-white text-[10px] rounded hover:bg-purple-700"
                                                        title="Renew Lease"
                                                    >
                                                        Renew
                                                    </button>
                                                    <button
                                                        onClick={() => terminateLease(lease.id)}
                                                        className="px-1.5 py-0.5 bg-red-600 text-white text-[10px] rounded hover:bg-red-700"
                                                        title="Terminate Lease"
                                                    >
                                                        Terminate
                                                    </button>
                                                </>
                                            )}
                                            {lease.status === 'terminated' && (
                                                <>
                                                    <button
                                                        onClick={() => viewLeaseDetails(lease)}
                                                        className="px-1.5 py-0.5 bg-indigo-600 text-white text-[10px] rounded hover:bg-indigo-700"
                                                        title="View Details"
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        onClick={() => viewPayments(lease)}
                                                        className="px-1.5 py-0.5 bg-gray-600 text-white text-[10px] rounded hover:bg-gray-700"
                                                        title="View Payments"
                                                    >
                                                        Payments
                                                    </button>
                                                </>
                                            )}
                                            {lease.status === 'active' && (
                                                <button
                                                    onClick={() => viewPayments(lease)}
                                                    className="px-1.5 py-0.5 bg-gray-600 text-white text-[10px] rounded hover:bg-gray-700"
                                                    title="View Payments"
                                                >
                                                    Payments
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {leases.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                                        {statusFilter === 'all' 
                                            ? 'No leases found. Create your first lease by clicking on an available unit.'
                                            : `No ${statusFilter} leases found.`}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        total={totalItems}
                        limit={PAGE_SIZE}
                        onPageChange={onPageChange}
                    />
                )}
            </div>

            
            {/* Create Lease Modal */}
            <Modal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setSelectedAsset(null);
                    reset();
                }}
                title={`Create Lease for ${selectedAsset?.name}${selectedAsset?.selectedUnit ? ` - Unit ${selectedAsset.selectedUnit.unit_number}` : ''}`}
                size="lg"
            >
                <form onSubmit={handleSubmit(onCreateLease)} className="space-y-6">
                    <input type="hidden" {...register('asset_unit_id')} value={selectedAsset?.selectedUnit?.id || ''} />
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <User className="inline h-4 w-4 mr-1" />
                            Select Tenant
                        </label>
                        <select
                            {...register('user_id', { required: 'Tenant is required' })}
                            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                            <option value="">Choose a tenant...</option>
                            {tenants.map(tenant => (
                                <option key={tenant.id} value={tenant.id}>
                                    {tenant.first_name} {tenant.last_name} - {tenant.email}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600">Property: <span className="font-medium">{selectedAsset?.name}</span></p>
                        <p className="text-sm text-gray-600">Unit: <span className="font-medium">{selectedAsset?.selectedUnit?.unit_number}</span></p>
                        <p className="text-sm text-gray-600">Base Rate: <span className="font-medium">KES {selectedAsset?.base_rate?.toLocaleString()}/month</span></p>
                        {selectedAsset?.selectedUnit?.rate_override && (
                            <p className="text-sm text-indigo-600">Custom Rate: <span className="font-medium">KES {selectedAsset.selectedUnit.rate_override.toLocaleString()}/month</span></p>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <Calendar className="inline h-4 w-4 mr-1" />
                                Start Date
                            </label>
                            <input
                                type="date"
                                {...register('start_date', { required: 'Start date is required' })}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Duration (Months)
                            </label>
                            <input
                                type="number"
                                {...register('duration_months', { required: 'Duration is required', min: 1 })}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="12"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            End Date (Auto-calculated)
                        </label>
                        <input
                            type="date"
                            {...register('end_date')}
                            readOnly
                            className="w-full rounded-lg border-gray-300 bg-gray-50 shadow-sm"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Billing Cycle
                        </label>
                        <select
                            {...register('billing_cycle')}
                            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                            <option value="monthly">Monthly</option>
                            <option value="weekly">Weekly</option>
                            <option value="daily">Daily</option>
                            <option value="termly">Termly (School Terms)</option>
                            <option value="milestone">Milestone (One-time)</option>
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <DollarSign className="inline h-4 w-4 mr-1" />
                                Rent Amount (KES)
                            </label>
                            <input
                                type="number"
                                {...register('rent_amount', { required: 'Rent amount is required', min: 0 })}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="25000"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Security Deposit (KES)
                            </label>
                            <input
                                type="number"
                                {...register('deposit_amount')}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="25000"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Payment Due Day
                            </label>
                            <input
                                type="number"
                                {...register('payment_due_day')}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="1"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Late Fee Percentage (%)
                            </label>
                            <input
                                type="number"
                                step="0.5"
                                {...register('late_fee_percentage')}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="5"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Terms & Conditions
                        </label>
                        <textarea
                            {...register('terms')}
                            rows={4}
                            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="Enter lease terms and conditions..."
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={() => {
                                setShowModal(false);
                                setSelectedAsset(null);
                                reset();
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                        >
                            {submitting ? 'Creating...' : 'Create Lease'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Renew Lease Modal */}
            <Modal
                isOpen={showRenewModal}
                onClose={() => {
                    setShowRenewModal(false);
                    setSelectedLeaseForRenew(null);
                }}
                title={`Renew Lease - ${selectedLeaseForRenew?.asset_name || ''} Unit ${selectedLeaseForRenew?.unit_number || ''}`}
                size="md"
            >
                <div className="space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-blue-800">
                            Current lease ends on <strong>{selectedLeaseForRenew?.end_date ? new Date(selectedLeaseForRenew.end_date).toLocaleDateString() : 'N/A'}</strong>
                        </p>
                        <p className="text-sm text-blue-800 mt-1">
                            Current rent: <strong>KES {parseFloat(selectedLeaseForRenew?.rent_amount || 0).toLocaleString()}</strong>/month
                        </p>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            New End Date *
                        </label>
                        <input
                            type="date"
                            value={renewFormData.new_end_date}
                            onChange={(e) => setRenewFormData({ ...renewFormData, new_end_date: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Rent Adjustment (KES)
                        </label>
                        <input
                            type="number"
                            value={renewFormData.rent_adjustment}
                            onChange={(e) => setRenewFormData({ ...renewFormData, rent_adjustment: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                            placeholder="0"
                            step="1000"
                        />
                        <p className="text-xs text-gray-500 mt-1">Positive = Increase, Negative = Decrease</p>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            New Monthly Rent
                        </label>
                        <div className="bg-gray-100 p-2 rounded text-lg font-bold text-indigo-600">
                            KES {(parseFloat(selectedLeaseForRenew?.rent_amount || 0) + parseFloat(renewFormData.rent_adjustment || 0)).toLocaleString()}
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Reason for Renewal (Optional)
                        </label>
                        <textarea
                            value={renewFormData.reason}
                            onChange={(e) => setRenewFormData({ ...renewFormData, reason: e.target.value })}
                            rows={2}
                            className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 p-2"
                            placeholder="e.g., Tenant requested extension, Lease renewal with adjustment..."
                        />
                    </div>
                    
                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            onClick={() => {
                                setShowRenewModal(false);
                                setSelectedLeaseForRenew(null);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleRenewLease}
                            disabled={submitting}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                        >
                            {submitting ? 'Processing...' : 'Renew Lease'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Payment Modal */}
            <PaymentModal
                isOpen={showPaymentModal}
                onClose={() => {
                    setShowPaymentModal(false);
                    setSelectedLeaseForPayment(null);
                }}
                lease={selectedLeaseForPayment}
                onPaymentComplete={() => {
                    fetchLeases();
                    fetchAssetsWithUnits();
                }}
            />

            {/* Transfer Modal */}
            <TransferModal
                isOpen={showTransferModal}
                onClose={() => {
                    setShowTransferModal(false);
                    setSelectedLeaseForTransfer(null);
                }}
                lease={selectedLeaseForTransfer}
                onTransferComplete={() => {
                    setShowTransferModal(false);
                    setSelectedLeaseForTransfer(null);
                    fetchLeases();
                    fetchAssetsWithUnits();
                }}
            />

            {/* Lease Details Modal */}
            <LeaseDetailsModal
                isOpen={showLeaseDetailsModal}
                onClose={() => {
                    setShowLeaseDetailsModal(false);
                    setSelectedLeaseForDetails(null);
                }}
                lease={selectedLeaseForDetails}
                onPaymentComplete={() => {
                    fetchLeases();
                    fetchAssetsWithUnits();
                }}
            />
        </div>
    );
};

export default Leases;
